import {
  useId,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
} from "react";
import type { SelectChangeEvent } from "@mui/material";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type {
  TranslationKey,
  TranslationKeyAndOptions,
} from "@thalamos/vision-common";
import { buildTranslationInput, tk, translate } from "@thalamos/vision-common";

import { Translation } from "../../Translation/Translation.js";
import { FormError } from "../FormError/FormError.js";
import { FormLabel } from "../FormLabel/FormLabel.js";

const debug = debugFn("vision-frontend:Dropdown");

type DropdownOption = {
  label: TranslationKeyAndOptions;
  value: string;
};

export type DropdownOutput =
  | {
      value: null;
      isUnknown: true;
    }
  | {
      value: string;
      valueLabel: string;
      isUnknown: false;
    }
  | {
      value: string;
      valueLabel: string;
    }
  | "";

export type DropdownProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  options: DropdownOption[];
  canBeUnknown?: boolean;
  isEditable?: boolean;
} & Pick<ComponentProps<typeof Select>, "autoFocus">;

export const Dropdown = ({
  fieldName,
  label,
  options,
  canBeUnknown,
  isEditable,
  autoFocus,
}: DropdownProps) => {
  const { t } = useTranslation();
  const ariaId = useId();
  const [field, meta, helpers] = useField<DropdownOutput>(fieldName);

  // We use this to store the last value that the user selected.
  // This is so that we can restore it if they turn on the "unknown" flag.
  // We pre-populate this with the incoming "initialValue".
  const [selectedValue, setSelectedValue] = useState<string | null>(
    field.value === "" ? null : field.value.value,
  );

  // Clean up the field value to be a structured object
  const fieldValue = useMemo(() => {
    const value =
      field.value === ""
        ? // Turn null-ish values into a structured object
          ({ value: null, valueLabel: null, isUnknown: false } as const)
        : "isUnknown" in field.value
          ? field.value.isUnknown
            ? // If "unknown", clear out the value and label
              ({ value: null, valueLabel: null, isUnknown: true } as const)
            : {
                value: field.value.value,
                valueLabel: field.value.valueLabel,
                isUnknown: field.value.isUnknown,
              }
          : { value: field.value.value, valueLabel: field.value.valueLabel };

    debug("Cleaned up value", { value });
    return value;
  }, [field.value]);

  const handleValueChange = (event: SelectChangeEvent) => {
    const runAsync = async () => {
      const selectedValue = event.target.value;

      const selectedValueLabel = translate(
        t,
        buildTranslationInput(
          options.find((option) => option.value === selectedValue)?.label ??
            ("" as TranslationKey),
        ),
      );

      const newValue = {
        value: selectedValue,
        valueLabel: selectedValueLabel,
        ...(canBeUnknown ? { isUnknown: false } : {}),
      };

      debug("handleValueChange, setting value", newValue);
      setSelectedValue(selectedValue);
      await helpers.setValue(newValue);
      await helpers.setTouched(true);
    };

    void runAsync();
  };

  const handleUnknownChange = (event: ChangeEvent<HTMLInputElement>) => {
    const runAsync = async () => {
      const selectedValueLabel = translate(
        t,
        buildTranslationInput(
          options.find((option) => option.value === selectedValue)?.label ??
            ("" as TranslationKey),
        ),
      );

      const newValue = event.target.checked
        ? ({
            value: null,
            valueLabel: null,
            isUnknown: true,
          } as const)
        : selectedValue
          ? ({
              value: selectedValue,
              valueLabel: selectedValueLabel,
              isUnknown: false,
            } as const)
          : "";

      debug("handleUnknownChange, Setting value", newValue);

      await helpers.setValue(newValue);
      await helpers.setTouched(true);
    };

    void runAsync();
  };

  return (
    <Box data-testid={`formField-${fieldName}`}>
      <FormControl fullWidth variant="outlined">
        {label && <FormLabel {...label} id={ariaId} />}
        <Select
          labelId={ariaId}
          name={fieldName}
          value={selectedValue ?? ""}
          onChange={handleValueChange}
          disabled={
            ("isUnknown" in fieldValue && fieldValue.isUnknown) ||
            isEditable === false
          }
          autoFocus={autoFocus}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              style={{ whiteSpace: "normal" }}
            >
              <Translation {...option.label} />
            </MenuItem>
          ))}
        </Select>
        {canBeUnknown && (
          <FormControlLabel
            control={
              <Switch
                checked={"isUnknown" in fieldValue && fieldValue.isUnknown}
                onChange={handleUnknownChange}
                data-testid={`formField-${fieldName}-unknown`}
              />
            }
            label={t(tk("common.unknown"))}
          />
        )}
        {meta.touched && meta.error && <FormError error={meta.error} />}
      </FormControl>
      {debug.enabled && (
        <>
          <Divider />
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"field.value : "}
            {JSON.stringify(field.value)}
          </Typography>
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"Treated like: "}
            {JSON.stringify(fieldValue)}
          </Typography>
          <Divider />
        </>
      )}
    </Box>
  );
};
